import { SBModel } from '@supplement-bacon/react-toolkit/models'

interface Setting<T = any> extends SBModel {
  key: string
  value: T
}

enum SettingKey {
  BOOKING_GENERIC_EMAIL = 'booking_generic_email',
  BOOKING_STATUS_TO_HIDE = 'booking_status_booking_to_hide',
  BOOKING_CANCELLED_STATUS = 'booking_cancelled_status',
  BOOKING_IS_UPDATABLE_TIME = 'booking_is_updatable_time',
  BOOKING_IS_CANCELLABLE_TIME = 'booking_is_cancellable_time',

  FEEDBACK_RESERVATION_STATUS = 'feedback_reservation_status', //remove sur V2

  MARKETING_NOSHOW_NOTIFICATION_TIME = 'marketing_noshow_notification_time',
  MARKETING_NOSHOW_NOTIFICATION_STATUS = 'marketing_noshow_notification_status',
  MARKETING_CONFIRMATION_TIME_IN_ADVANCE = 'marketing_confirmation_time_in_advance',
  MARKETING_CONFIRMATION_CONFIRMATION_STATUS = 'marketing_confirmation_confirmation_status',
  MARKETING_FEEDBACK_NOTIFICATION_TIME = 'marketing_feedback_notification_time',
  MARKETING_FEEDBACK_NOTIFICATION_STATUS = 'marketing_feedback_notification_status',

  MEMBERSHIP_SPONSORED_RULE = 'membership_sponsored_rule',
  MEMBERSHIP_SPONSORED_POINTS = 'membership_sponsored_points',
  MEMBERSHIP_SPONSORED_CREDITS = 'membership_sponsored_credits',
  MEMBERSHIP_SPONSORED_WILL_GET_CREDITS = 'membership_sponsored_will_get_credits',
  MEMBERSHIP_CODE_URL = 'membership_code_url',
  MEMBERSHIP_LOYALTY_TIER = 'membership_loyalty_tier',
  MEMBERSHIP_NOSHOW_NOTIFICATION_ENABLED = 'membership_noshow_notification_enabled',
  MEMBERSHIP_NOSHOW_NOTIFICATION_TIME = 'membership_noshow_notification_time',
  MEMBERSHIP_CONFIRMATION_CONFIRMATION_STATUS = 'membership_confirmation_confirmation_status',
  MEMBERSHIP_CONFIRMATION_TIME_IN_ADVANCE = 'membership_confirmation_time_in_advance',
  MEMBERSHIP_BIRTHDAY_NOTIFICATION_TIME = 'membership_birthday_notification_time',
  MEMBERSHIP_BIRTHDAY_MODAL = 'membership_birthday_modal',
  /** Managed through its own upload endpoints, not the generic setting form. */
  MEMBERSHIP_DEMO_BOOKLET = 'membership_demo_booklet',

  USERS_MINIMUM_POINTS_GUARANTEE = 'users_minimum_points_guarantee',

  GDPR_PURGE_ENABLED = 'gdpr_purge_enabled',
  GDPR_PURGE_RETENTION_DURATION = 'gdpr_purge_retention_duration',
  GDPR_PURGE_NOTICE_ENABLED = 'gdpr_purge_notice_enabled',
  GDPR_PURGE_NOTICE_DELAY = 'gdpr_purge_notice_delay',

  CHAT_VISIBILITY = 'chat_visibility',

  APP_MINIMUM_VERSION_IOS = 'app_minimum_version',
  APP_MINIMUM_VERSION_ANDROID = 'app_minimum_version_android',

  // Notifications
  NOTFICATION_RESERVATION_PLACED_ENABLED = 'notification_reservation_placed_enabled',
  NOTFICATION_RESERVATION_UPDATED_ENABLED = 'notification_reservation_updated_enabled',
  NOTFICATION_RESERVATION_CANCELLED_ENABLED = 'notification_reservation_cancelled_enabled',
  NOTFICATION_RESERVATION_CONFIRMATION_ENABLED = 'notification_reservation_confirmation_enabled',
  NOTFICATION_RESERVATION_NO_SHOW_ENABLED = 'notification_reservation_no_show_enabled',

  BUSINESS_LOYALTY_TIER = 'business_loyalty_tier',
  BUSINESS_TRIVEC_PAYMENT_METHOD = 'business_trivec_payment_method',
  BUSINESS_TRIVEC_ACCOUNT_TYPE = 'business_trivec_account_type',

  // Integrations
  INTEGRATIONS_UBER_ENABLED = 'integrations_uber_enabled',

  // Web booking
  WEB_BOOKING_ENABLED = 'web_booking_enabled',
}

const booleanSettingKeys = [
  SettingKey.CHAT_VISIBILITY,
  SettingKey.NOTFICATION_RESERVATION_PLACED_ENABLED,
  SettingKey.NOTFICATION_RESERVATION_CONFIRMATION_ENABLED,
  SettingKey.NOTFICATION_RESERVATION_UPDATED_ENABLED,
  SettingKey.NOTFICATION_RESERVATION_NO_SHOW_ENABLED,
  SettingKey.NOTFICATION_RESERVATION_CANCELLED_ENABLED,
  SettingKey.MEMBERSHIP_NOSHOW_NOTIFICATION_ENABLED,
  SettingKey.GDPR_PURGE_ENABLED,
  SettingKey.GDPR_PURGE_NOTICE_ENABLED,
  SettingKey.INTEGRATIONS_UBER_ENABLED,
  SettingKey.WEB_BOOKING_ENABLED,
]

enum SettingKeyInputType {
  inputText = 'inputText',
  delay = 'delay',
  inputNumber = 'inputNumber',
  delaySelect = 'delaySelect',
  tagEditor = 'tagEditor',
  switch = 'switch',
  appVersion = 'appVersion',
  loyaltyTiers = 'loyaltyTiers',
  notificationSwitch = 'notificationSwitch',
  notificationSegmented = 'notificationSegmented',
  membershipConfirmationStatus = 'membershipConfirmationStatus',
  integrationSwitch = 'integrationSwitch',
}

const settingsByType: Partial<Record<SettingKey, SettingKeyInputType>> = {
  // inputText
  [SettingKey.MARKETING_CONFIRMATION_CONFIRMATION_STATUS]:
    SettingKeyInputType.inputText,
  [SettingKey.MARKETING_NOSHOW_NOTIFICATION_STATUS]:
    SettingKeyInputType.inputText,
  [SettingKey.BOOKING_CANCELLED_STATUS]: SettingKeyInputType.inputText,
  [SettingKey.BUSINESS_TRIVEC_PAYMENT_METHOD]: SettingKeyInputType.inputText,
  [SettingKey.BOOKING_GENERIC_EMAIL]: SettingKeyInputType.inputText,

  // inputNumber
  [SettingKey.GDPR_PURGE_RETENTION_DURATION]: SettingKeyInputType.inputNumber,
  [SettingKey.GDPR_PURGE_NOTICE_DELAY]: SettingKeyInputType.inputNumber,
  [SettingKey.BUSINESS_TRIVEC_ACCOUNT_TYPE]: SettingKeyInputType.inputNumber,

  // delay
  [SettingKey.MARKETING_CONFIRMATION_TIME_IN_ADVANCE]:
    SettingKeyInputType.delay,
  [SettingKey.MARKETING_NOSHOW_NOTIFICATION_TIME]: SettingKeyInputType.delay,
  [SettingKey.BOOKING_IS_UPDATABLE_TIME]: SettingKeyInputType.delay,
  [SettingKey.BOOKING_IS_CANCELLABLE_TIME]: SettingKeyInputType.delay,
  [SettingKey.MEMBERSHIP_CONFIRMATION_TIME_IN_ADVANCE]:
    SettingKeyInputType.delay,
  [SettingKey.MEMBERSHIP_NOSHOW_NOTIFICATION_TIME]: SettingKeyInputType.delay,

  // delaySelect
  [SettingKey.MEMBERSHIP_BIRTHDAY_NOTIFICATION_TIME]:
    SettingKeyInputType.delaySelect,

  // tagEditor
  [SettingKey.BOOKING_STATUS_TO_HIDE]: SettingKeyInputType.tagEditor,

  // appVersion
  [SettingKey.APP_MINIMUM_VERSION_IOS]: SettingKeyInputType.appVersion,
  [SettingKey.APP_MINIMUM_VERSION_ANDROID]: SettingKeyInputType.appVersion,

  // loyaltyTiers
  [SettingKey.MEMBERSHIP_LOYALTY_TIER]: SettingKeyInputType.loyaltyTiers,
  [SettingKey.BUSINESS_LOYALTY_TIER]: SettingKeyInputType.loyaltyTiers,

  // switch
  [SettingKey.CHAT_VISIBILITY]: SettingKeyInputType.switch,

  // integrationSwitch
  [SettingKey.INTEGRATIONS_UBER_ENABLED]:
    SettingKeyInputType.integrationSwitch,
  [SettingKey.WEB_BOOKING_ENABLED]: SettingKeyInputType.integrationSwitch,

  // notificationSwitch
  [SettingKey.NOTFICATION_RESERVATION_PLACED_ENABLED]:
    SettingKeyInputType.notificationSwitch,
  [SettingKey.NOTFICATION_RESERVATION_UPDATED_ENABLED]:
    SettingKeyInputType.notificationSwitch,
  [SettingKey.NOTFICATION_RESERVATION_CANCELLED_ENABLED]:
    SettingKeyInputType.notificationSwitch,
  [SettingKey.NOTFICATION_RESERVATION_CONFIRMATION_ENABLED]:
    SettingKeyInputType.notificationSwitch,
  [SettingKey.NOTFICATION_RESERVATION_NO_SHOW_ENABLED]:
    SettingKeyInputType.notificationSwitch,
  [SettingKey.MEMBERSHIP_NOSHOW_NOTIFICATION_ENABLED]:
    SettingKeyInputType.notificationSwitch,

  // notificationSegmented
  [SettingKey.GDPR_PURGE_ENABLED]: SettingKeyInputType.notificationSegmented,
  [SettingKey.GDPR_PURGE_NOTICE_ENABLED]:
    SettingKeyInputType.notificationSegmented,

  // membershipConfirmationStatus
  [SettingKey.MEMBERSHIP_CONFIRMATION_CONFIRMATION_STATUS]:
    SettingKeyInputType.membershipConfirmationStatus,
}

export { booleanSettingKeys, SettingKey, SettingKeyInputType, settingsByType }
export type { Setting }
